import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	selectIsAuthenticated,
	selectAuthInitializing,
} from "../../redux/features/authSlice";
import { Spin } from "antd";
import { useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase/config";
import {
	setColorsListGlobally,
	setProductsListGlobally,
} from "../../redux/features/globalSelectsSlice";

/**
 * ProtectedRoute
 *
 * Wraps any route that requires authentication.
 * - While Firebase is initializing → show spinner (prevents flash)
 * - If not authenticated → redirect to /login, preserving the intended URL
 * - If authenticated → render the child route
 *
 * Usage in your router:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/orders/manage" element={<ManageOrdersPage />} />
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *     ...all other protected routes...
 *   </Route>
 */
const ProtectedRoute = () => {
	const isAuthenticated = useSelector(selectIsAuthenticated);
	const initializing = useSelector(selectAuthInitializing);
	const location = useLocation();
	const dispatch = useDispatch();

	useEffect(() => {
		if (isAuthenticated) {
			async function fetchGlobalSelects() {
				// get products list
				const products_snapshot = await getDocs(
					collection(db, "productsList"),
				);
				const products_raw = products_snapshot.docs.map((d) => ({
					id: d.id,
					key: d.id,
					...d.data(),
				}));
				const products_sorted = products_raw.sort((a, b) =>
					a.productName
						.toLowerCase()
						.localeCompare(b.productName.toLowerCase()),
				);
				// get colors list
				const colors_snapshot = await getDocs(collection(db, "colors"));
				const colors_raw = colors_snapshot.docs.map((d) => ({
					id: d.id,
					key: d.id,
					...d.data(),
				}));
				const colors_sorted = colors_raw.sort((a, b) =>
					a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
				);
				// dispatch to global state
				dispatch(setProductsListGlobally(products_sorted));
				dispatch(setColorsListGlobally(colors_sorted));
			}
			fetchGlobalSelects();
		}
	}, []);

	if (initializing) {
		return (
			<div
				style={{
					height: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#0f1117",
				}}
			>
				<Spin size="large" />
			</div>
		);
	}

	if (!isAuthenticated) {
		// Preserve where the user was trying to go — redirect back after login
		return <Navigate to="/login" state={{ from: location }} replace />;
	}

	return <Outlet />;
};

export default ProtectedRoute;
